<template>
  <v-card>
    <v-alert :type="alertType" v-if="alert">{{ alertMessage }}</v-alert>
    <v-container class="py-0" fluid>
      <v-row align-content="space-around">
        <v-col cols="12" sm="2">
          <v-card-title class="pa-0">
            <div>
              <div class="headline">Referrals</div>
              <div class="text-subtitle-1 font-weight-thin" v-if="listView">
                {{ caseCountLabel }}
              </div>
            </div>
          </v-card-title>
        </v-col>
        <v-col cols="12" sm="3">
          <v-radio-group v-model="listView" row>
            <v-radio label="List" :value="true"></v-radio>
            <v-radio label="Aggregate" :value="false"></v-radio>
          </v-radio-group>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="2" v-if="listView">
          <v-radio-group v-model="referredBy" row>
            <v-radio label="From" :value="true"></v-radio>
            <v-radio label="To" :value="false"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            single-line
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="auto" class="d-flex align-center" v-if="!listView">
          <v-btn small color="primary" @click="showGroupsManager = true">
            <v-icon left small>mdi-account-group</v-icon>
            Manage Groups
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :footer-props="{
        'items-per-page-options': [-1]
      }"
      :headers="headers"
      hide-default-footer
      :items="referrals"
      :loading="$apollo.loading"
      :search="search"
      :sort-by="referredBy ? ['retainedDate'] : ['referredDate']"
      :sort-desc="[false]"
      v-if="listView"
      @current-items="setDisplayedCases"
    >
      <template v-slot:item.caseId="{ item }">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${item.caseId}`
          "
        >
          <v-icon size="15">mdi-open-in-new</v-icon>
        </a>
      </template>
      <template v-slot:item.referralSourceOther="{ item }">
        {{ item.referralSourceOther }}
        <v-icon small color="green" v-if="item.contact.name"
          >mdi-alpha-c-circle</v-icon
        >
        <v-icon
          small
          v-if="!item.contact.name"
          @click="openNewContactDialog(item)"
          >mdi-plus</v-icon
        >
      </template>
      <template v-slot:item.medicalProvider="{ item }">
        {{ item.medicalProvider }}
        <v-icon small color="green" v-if="item.contact.name"
          >mdi-alpha-c-circle</v-icon
        >
        <v-icon
          small
          v-if="!item.contact.name"
          @click="openNewContactDialog(item)"
          >mdi-plus</v-icon
        >
      </template>
    </v-data-table>
    <v-card v-else v-for="market in referrals" :key="market.office">
      <v-card-title>
        {{ market.office }}
      </v-card-title>
      <v-data-table
        :footer-props="{
          'items-per-page-options': [-1]
        }"
        :headers="headers"
        hide-default-footer
        :items="market.medicalProviders"
        :loading="$apollo.loading"
        :search="search"
        :sort-by="['medicalProvider']"
        :sort-desc="[false]"
      >
        <template v-slot:item.medicalProvider="{ item }">
          <a v-if="item.isGroup" @click="openGroupDialog(item, market.office)">
            <v-icon small color="primary" class="mr-1"
              >mdi-account-group</v-icon
            >
            {{ item.medicalProvider }}
            <span class="grey--text text-caption"
              >({{ item.members.length }})</span
            >
          </a>
          <span v-else>{{ item.medicalProvider }}</span>
        </template>
        <template v-slot:item.referredTo="{ item }">
          <a
            v-if="item.isGroup"
            @click="openGroupDialog(item, market.office)"
            >{{ item.referredTo }}</a
          >
          <a v-else @click="openAggDialog(item, 'to', market.office)">{{
            item.referredTo
          }}</a>
        </template>
        <template v-slot:item.referredBy="{ item }">
          <a
            v-if="item.isGroup"
            @click="openGroupDialog(item, market.office)"
            >{{ item.referredBy }}</a
          >
          <a v-else @click="openAggDialog(item, 'by', market.office)">{{
            item.referredBy
          }}</a>
        </template>
        <template v-slot:item.difference="{ item }">
          <v-chip
            x-small
            :color="item.referredTo - item.referredBy < 0 ? 'red' : 'green'"
            >{{ item.referredTo - item.referredBy }}</v-chip
          >
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="dialog" width="80%">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ dialogProvider }}
        </v-card-title>

        <v-data-table
          :footer-props="{
            'items-per-page-options': [-1]
          }"
          :headers="dialogHeaders"
          hide-default-footer
          :items="dialogList"
          :loading="$apollo.loading"
          :sort-by="
            dialogDirection === 'by' ? ['retainedDate'] : ['referredDate']
          "
          :sort-desc="[false]"
        >
          <template v-slot:item.caseId="{ item }">
            <a
              class="go-to-case-link mr-2"
              target="_blank"
              :href="
                `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${item.caseId}`
              "
            >
              <v-icon size="15">mdi-open-in-new</v-icon>
            </a>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>
    <v-dialog v-model="contactDialog" hide-overlay width="300">
      <v-card>
        <v-card-title>New Contact</v-card-title>
        <v-card-text>
          {{ selectedContact.referralSourceOther }}
        </v-card-text>
        <v-card-text>
          <v-select
            :items="contactTypeOptions"
            label="Contact Type"
            v-model="selectedContactType"
            dense
          ></v-select>
          <v-btn
            @click="addContact"
            color="primary"
            :disabled="!selectedContactType"
            >Add Contact</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="groupDialog" width="60%">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ groupProvider }}
          <v-spacer></v-spacer>
          <span class="text-subtitle-2 grey--text">Group breakdown</span>
        </v-card-title>
        <v-data-table
          :footer-props="{
            'items-per-page-options': [-1]
          }"
          :headers="groupBreakdownHeaders"
          hide-default-footer
          :items="groupMembers"
          :sort-by="['medicalProvider']"
          :sort-desc="[false]"
        >
          <template v-slot:item.referredTo="{ item }">
            <a @click="openAggDialog(item, 'to', groupOffice)">{{
              item.referredTo
            }}</a>
          </template>
          <template v-slot:item.referredBy="{ item }">
            <a @click="openAggDialog(item, 'by', groupOffice)">{{
              item.referredBy
            }}</a>
          </template>
          <template v-slot:item.difference="{ item }">
            <v-chip
              x-small
              :color="item.referredTo - item.referredBy < 0 ? 'red' : 'green'"
              >{{ item.referredTo - item.referredBy }}</v-chip
            >
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>
    <referral-groups-manager
      v-model="showGroupsManager"
      :contact-options="contactOptions"
      @groups-changed="$apollo.queries.referralGroups.refetch()"
    />
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import filter from "lodash/filter";
import groupBy from "lodash/groupBy";
import findIndex from "lodash/findIndex";
import uniqBy from "lodash/uniqBy";
import { mapGetters } from "vuex";
import { DASH_INTAKE_REFERRALS } from "../graphql/intake.gql";
import ReferralGroupsManager from "./ReferralGroupsManager";

export default {
  components: {
    ReferralGroupsManager
  },
  data() {
    return {
      alert: false,
      alertType: false,
      alertMessage: false,
      search: "",
      displayedCaseCount: null,
      organization: { newCasesInPeriod: [] },
      referralGroups: [],
      referredBy: true,
      listView: true,
      dialog: false,
      dialogProvider: "",
      dialogDirection: "",
      dialogList: [],
      contactDialog: false,
      selectedContactType: false,
      selectedContact: false,
      groupDialog: false,
      groupProvider: "",
      groupMembers: [],
      groupOffice: "",
      showGroupsManager: false
    };
  },
  methods: {
    setDisplayedCases(items) {
      this.displayedCaseCount = Array.isArray(items) ? items.length : 0;
    },
    openAggDialog(item, direction, office) {
      this.dialogProvider = item.medicalProvider;
      this.dialogDirection = direction;
      let hasIdentity = item.saId != null && item.saCtg != null;
      let inOffice = c => office == null || c.office === office;
      if (direction === "to") {
        this.dialogList = filter(this.referredTo, c => {
          if (!inOffice(c)) return false;
          return hasIdentity
            ? c.saContactId === item.saId && c.saContactCtg === item.saCtg
            : c.medicalProvider === item.medicalProvider;
        });
      } else {
        this.dialogList = filter(this.refBy, c => {
          if (!inOffice(c)) return false;
          return hasIdentity
            ? c.referralSourceOtherContactId === item.saId &&
                c.referralSourceOtherContactCtg === item.saCtg
            : c.referralSourceOther === item.medicalProvider;
        });
      }
      this.dialog = true;
    },
    openGroupDialog(item, office) {
      this.groupProvider = item.medicalProvider;
      this.groupMembers = item.members || [];
      this.groupOffice = office;
      this.groupDialog = true;
    },
    openNewContactDialog(item) {
      this.contactDialog = true;
      this.selectedContact = item;
    },
    addContact() {
      let contact = {
        sa_id:
          this.selectedContact.referralSourceOtherContactId ||
          this.selectedContact.saContactId,
        sa_ctg:
          this.selectedContact.referralSourceOtherContactCtg ||
          this.selectedContact.saContactCtg,
        contact_type_id: parseInt(this.selectedContactType, 10)
      };
      this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateContact($contact: ContactInput!) {
              createContact(contact: $contact) {
                id
                name
              }
            }
          `,
          variables: {
            contact
          },
          update: () => {
            this.$apollo.queries.organization.refetch();
            this.$apollo.queries.referredTo.refetch();
          }
        })
        .then(() => {
          this.contactDialog = false;
          this.selectedContact = false;
          this.selectedContactType = false;
        })
        .catch(error => {
          console.error(error);
          this.alert = true;
          this.alertType = "error";
          this.alertMessage = error.message;
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        });
      return false;
    }
  },
  computed: {
    ...mapGetters(["currentUser", "startDate", "endDate"]),
    totalRows() {
      return this.organization.newCasesInPeriod.length;
    },
    hasActiveSearch() {
      return typeof this.search === "string" && this.search.trim().length > 0;
    },
    caseCountLabel() {
      const total = this.referrals.length;
      if (this.hasActiveSearch && this.displayedCaseCount !== null) {
        return `${this.displayedCaseCount} of ${total} Cases`;
      }
      return `${total} Cases`;
    },
    groupBreakdownHeaders() {
      return [
        { text: "Contact", value: "medicalProvider", width: "300px" },
        { text: "To", value: "referredTo", width: "80px" },
        { text: "From", value: "referredBy", width: "80px" },
        { text: "Difference", value: "difference" }
      ];
    },
    contactOptions() {
      let opts = [];
      (this.refBy || []).forEach(c => {
        if (
          c.referralSourceOtherContactId != null &&
          c.referralSourceOtherContactCtg != null
        ) {
          opts.push({
            saId: c.referralSourceOtherContactId,
            saCtg: c.referralSourceOtherContactCtg,
            name: c.referralSourceOther
          });
        }
      });
      (this.referredTo || []).forEach(c => {
        if (c.saContactId != null && c.saContactCtg != null) {
          opts.push({
            saId: c.saContactId,
            saCtg: c.saContactCtg,
            name: c.medicalProvider
          });
        }
      });
      return uniqBy(opts, o => `${o.saCtg}:${o.saId}`);
    },
    dialogHeaders() {
      if (this.dialogDirection === "by") {
        return [
          { text: "Action", value: "caseId", width: "100px" },
          { text: "Client", value: "clientName" },
          { text: "Type", value: "referralSource" },
          { text: "Referred From", value: "referralSourceOther" },
          { text: "Attorney", value: "attorney" },
          { text: "CM", value: "paralegal" },
          { text: "Retained", value: "retainedDate" },
          { text: "State", value: "state" },
          { text: "Office", value: "office" }
        ];
      } else {
        return [
          { text: "Action", value: "caseId", width: "100px" },
          { text: "Client", value: "clientName" },
          { text: "Referred To", value: "medicalProvider" },
          { text: "Attorney", value: "attorney" },
          { text: "CM", value: "paralegal" },
          { text: "Referred", value: "referredDate" },
          { text: "Office", value: "office" }
        ];
      }
    },
    headers() {
      if (this.listView) {
        if (this.referredBy) {
          return [
            { text: "Action", value: "caseId", width: "100px" },
            { text: "Client", value: "clientName" },
            { text: "Type", value: "referralSource" },
            { text: "Referred From", value: "referralSourceOther" },
            { text: "Attorney", value: "attorney" },
            { text: "CM", value: "paralegal" },
            { text: "Retained", value: "retainedDate" },
            { text: "State", value: "state" },
            { text: "Office", value: "office" }
          ];
        } else {
          return [
            { text: "Action", value: "caseId", width: "100px" },
            { text: "Client", value: "clientName" },
            { text: "Referred To", value: "medicalProvider" },
            { text: "Attorney", value: "attorney" },
            { text: "CM", value: "paralegal" },
            { text: "Referred", value: "referredDate" },
            { text: "Office", value: "office" }
          ];
        }
      } else {
        return [
          {
            text: "Referral Partner",
            value: "medicalProvider",
            width: "300px"
          },
          { text: "To", value: "referredTo", width: "80px" },
          { text: "From", value: "referredBy", width: "80px" },
          { text: "Difference", value: "difference" }
        ];
      }
    },
    refBy() {
      if (!this.organization) return [];
      return filter(this.organization.newCasesInPeriod, c => {
        return (
          c.referralSource === "Ref-Doctors" ||
          c.referralSource === "Ref-Auto Body" ||
          c.referralSource === "Ref-Tow Truck" ||
          c.referralSource === "Ref-BNI" ||
          c.referralSource === "Ref-Partners"
        );
      });
    },
    referrals() {
      let refByGroupedByOffice = groupBy(this.refBy, "office");
      let refByGroupedByOfficeProvider = Object.keys(refByGroupedByOffice).map(
        k => {
          let groupedBySource = groupBy(
            refByGroupedByOffice[k],
            "referralSourceOther"
          );
          let providersAndTotalReferredBy = Object.keys(groupedBySource).map(
            innerKey => {
              let cases = groupedBySource[innerKey];
              return {
                medicalProvider: innerKey,
                referredBy: cases.length,
                saId: cases[0].referralSourceOtherContactId,
                saCtg: cases[0].referralSourceOtherContactCtg
              };
            }
          );
          return { office: k, medicalProviders: providersAndTotalReferredBy };
        }
      );
      let refToGroupedByOffice = groupBy(this.referredTo, "office");
      let refToGroupedByOfficeProvider = Object.keys(refToGroupedByOffice).map(
        k => {
          let groupedBySource = groupBy(
            refToGroupedByOffice[k],
            "medicalProvider"
          );
          let providersAndTotalReferredTo = Object.keys(groupedBySource).map(
            innerKey => {
              let cases = groupedBySource[innerKey];
              return {
                medicalProvider: innerKey,
                referredTo: cases.length,
                saId: cases[0].saContactId,
                saCtg: cases[0].saContactCtg
              };
            }
          );
          return { office: k, medicalProviders: providersAndTotalReferredTo };
        }
      );
      let refByOffices = Object.keys(
        groupBy(refByGroupedByOfficeProvider, "office")
      );
      let refToOffices = Object.keys(
        groupBy(refToGroupedByOfficeProvider, "office")
      );
      let combinedOffices = [...new Set([...refByOffices, ...refToOffices])];

      let memberToGroup = {};
      (this.referralGroups || []).forEach(g => {
        (g.members || []).forEach(m => {
          memberToGroup[`${m.saCtg}:${m.saId}`] = g.id;
        });
      });

      let aggregateData = combinedOffices.map(o => {
        let byOfficeIndex = findIndex(refByGroupedByOfficeProvider, x => {
          return x.office === o;
        });
        let toOfficeIndex = findIndex(refToGroupedByOfficeProvider, x => {
          return x.office === o;
        });
        let byProviders = refByGroupedByOfficeProvider[byOfficeIndex]
          ? refByGroupedByOfficeProvider[byOfficeIndex].medicalProviders
          : [];
        let toProviders = refToGroupedByOfficeProvider[toOfficeIndex]
          ? refToGroupedByOfficeProvider[toOfficeIndex].medicalProviders
          : [];

        let byToProviders = byProviders.concat(toProviders);
        let medicalProviders = byToProviders.map(p => {
          return filter(byToProviders, pi => {
            return pi.medicalProvider === p.medicalProvider;
          }).reduce(
            (prev, cur) => {
              return Object.assign(prev, cur, {});
            },
            { referredTo: 0, referredBy: 0 }
          );
        });

        let noDuplicates = uniqBy(medicalProviders, "medicalProvider");

        // Providers that belong to a group are removed from the standalone
        // list and represented by a single group row instead.
        let standalone = noDuplicates.filter(p => {
          let key =
            p.saCtg != null && p.saId != null ? `${p.saCtg}:${p.saId}` : null;
          return !(key && memberToGroup[key]);
        });

        // Build group rows from the full stored membership, counting each
        // member by its (sa_ctg, sa_id) identity against this office's cases.
        // This keeps the member count consistent with the group definition and
        // is robust to provider name changes / duplicate names.
        let refByCases = refByGroupedByOffice[o] || [];
        let refToCases = refToGroupedByOffice[o] || [];
        let groupRows = [];
        (this.referralGroups || []).forEach(g => {
          let members = (g.members || []).map(m => {
            let key = `${m.saCtg}:${m.saId}`;
            let referredBy = filter(refByCases, c => {
              return (
                `${c.referralSourceOtherContactCtg}:${c.referralSourceOtherContactId}` ===
                key
              );
            }).length;
            let referredTo = filter(refToCases, c => {
              return `${c.saContactCtg}:${c.saContactId}` === key;
            }).length;
            return {
              medicalProvider: m.name,
              saId: m.saId,
              saCtg: m.saCtg,
              referredTo,
              referredBy
            };
          });
          let referredTo = members.reduce((s, x) => s + x.referredTo, 0);
          let referredBy = members.reduce((s, x) => s + x.referredBy, 0);
          if (referredTo > 0 || referredBy > 0) {
            groupRows.push({
              medicalProvider: g.name,
              isGroup: true,
              groupId: g.id,
              referredTo,
              referredBy,
              members
            });
          }
        });

        let withGroups = standalone.concat(groupRows);

        return { office: o, medicalProviders: withGroups };
      });

      if (this.listView) {
        if (this.referredBy) {
          return this.refBy;
        } else {
          return this.referredTo;
        }
      } else {
        return aggregateData;
      }
    },
    contactTypeOptions() {
      if (!this.contactTypes) return [];
      return this.contactTypes.map(c => {
        return {
          text: c.name,
          value: c.id
        };
      });
    }
  },
  apollo: {
    organization: {
      query: DASH_INTAKE_REFERRALS,
      variables() {
        return {
          id: this.currentUser.organization.id,
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      skip() {
        return this.currentUser ? false : true;
      }
    },
    referralGroups: {
      query: gql`
        query ReferralGroups {
          referralGroups {
            id
            name
            members {
              saId
              saCtg
              name
            }
          }
        }
      `
    },
    referredTo: {
      query: gql`
        query DashIntakeReferralsTo($startDate: String!, $endDate: String!) {
          referredTo: medicalReferredToInPeriod(
            startDate: $startDate
            endDate: $endDate
          ) {
            id
            caseId
            saContactId
            saContactCtg
            medicalProvider
            referredDate
            attorney
            paralegal
            clientName
            office
            contact {
              id
              name
            }
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      skip() {
        return this.currentUser ? false : true;
      }
    },
    contactTypes: {
      query: gql`
        query ContactTypes {
          contactTypes {
            id
            name
          }
        }
      `
    }
  }
};
</script>

<style scoped></style>
